<template>
  <div>
    <!-- Filters -->
    <v-row class="mb-2" align="center">
      <v-col cols="12" sm="4">
        <v-select
          v-model="filters.level"
          :items="levelOptions"
          :label="$translate('Level')"
          clearable
          multiple
          dense
          outlined
          hide-details
          @change="loadEvents"
        >
          <template v-slot:selection="{ item }">
            <v-chip :color="getLevelColor(item.value)" small dark class="mr-1">
              {{ item.value }}
            </v-chip>
          </template>
          <template v-slot:item="{ item }">
            <v-chip :color="getLevelColor(item.value)" small dark>
              {{ item.value }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="filters.state"
          :items="stateOptions"
          :label="$translate('State')"
          clearable
          multiple
          dense
          outlined
          hide-details
          @change="loadEvents"
        >
          <template v-slot:selection="{ item }">
            <v-chip :color="getStateColor(item.value)" small outlined class="mr-1">
              {{ item.text }}
            </v-chip>
          </template>
          <template v-slot:item="{ item }">
            <v-chip :color="getStateColor(item.value)" small outlined>
              {{ item.text }}
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" sm="4" class="d-flex align-center">
        <v-btn
          v-if="selectedEvents.length > 0"
          color="primary"
          small
          @click="showBulkDialog = true"
        >
          {{ $translate('Change state') }} ({{ selectedEvents.length }})
        </v-btn>
        <v-btn
          v-if="selectedEvents.length > 0"
          color="error"
          small
          class="ml-2"
          @click="showDeleteDialog = true"
        >
          {{ $translate('Delete') }} ({{ selectedEvents.length }})
        </v-btn>
        <v-btn
          icon
          small
          class="ml-2"
          @click="loadEvents"
          :loading="loading"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Events table -->
    <v-data-table
      v-model="selectedEvents"
      :headers="headers"
      :items="events"
      :loading="loading"
      :server-items-length="total"
      :options.sync="tableOptions"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      :mobile-breakpoint="800"
      show-select
      item-key="id"
      class="elevation-1"
      @click:row="showEventDetails"
    >
      <template v-slot:item.created="{ item }">
        <span class="text-no-wrap">{{ formatDate(item.created) }}</span>
      </template>

      <template v-slot:item.level="{ item }">
        <v-chip
          :color="getLevelColor(item.level)"
          small
          dark
        >
          {{ item.level }}
        </v-chip>
      </template>

      <template v-slot:item.source="{ item }">
        <div>
          <span class="font-weight-medium">{{ item.source }}</span>
          <div v-if="item.source_name && item.source !== 'custom'" class="text-caption grey--text text-truncate" style="max-width: 100px;">
            {{ item.source_name }}
          </div>
        </div>
      </template>

      <template v-slot:item.message="{ item }">
        <span :title="item.message">{{ item.message }}</span>
      </template>

      <template v-slot:item.occurrence_count="{ item }">
        {{ item.occurrence_count || 1 }}
      </template>

      <template v-slot:item.state="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              :color="getStateColor(item.state)"
              small
              outlined
              v-bind="attrs"
              v-on="on"
              @click.stop
            >
              {{ $translate(item.state) }}
              <v-icon small right>mdi-chevron-down</v-icon>
            </v-chip>
          </template>
          <v-list dense>
            <v-list-item
              v-for="state in ['new', 'acknowledged', 'resolved']"
              :key="state"
              @click="changeEventState(item, state)"
            >
              <v-list-item-content>
                <v-chip :color="getStateColor(state)" small outlined>
                  {{ $translate(state) }}
                </v-chip>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <!-- Event details dialog -->
    <v-dialog v-model="showDetailsDialog" max-width="700" scrollable>
      <v-card v-if="selectedEvent">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ $translate('Event Details') }}</span>
          <v-btn icon small @click="showDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="event-details-content">
          <v-simple-table>
            <tbody>
              <tr>
                <td class="font-weight-bold" width="120">{{ $translate('Time') }}</td>
                <td>{{ formatDateFull(selectedEvent.created) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $translate('Level') }}</td>
                <td>
                  <v-chip :color="getLevelColor(selectedEvent.level)" small dark>
                    {{ selectedEvent.level }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $translate('Source') }}</td>
                <td>{{ selectedEvent.source }} - {{ selectedEvent.source_name }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $translate('Domain') }}</td>
                <td>
                  <template v-if="selectedEvent.affected_domains && selectedEvent.affected_domains.length > 0">
                    <div v-for="domain in selectedEvent.affected_domains" :key="domain.id">
                      {{ domain.name }}
                    </div>
                  </template>
                  <template v-else>
                    {{ selectedEvent.domain_name || '-' }}
                  </template>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">{{ $translate('State') }}</td>
                <td>
                  <v-chip :color="getStateColor(selectedEvent.state)" small outlined>
                    {{ $translate(selectedEvent.state) }}
                  </v-chip>
                </td>
              </tr>
              <tr v-if="selectedEvent.occurrence_count > 1">
                <td class="font-weight-bold">{{ $translate('Events') }}</td>
                <td>{{ selectedEvent.occurrence_count }} ({{ $translate('last') }}: {{ formatDateFull(selectedEvent.last_occurrence) }})</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="mt-4">
            <div class="font-weight-bold mb-2">{{ $translate('Message') }}</div>
            <v-sheet class="pa-3 grey lighten-4" rounded>
              <pre class="message-pre">{{ selectedEvent.message }}</pre>
            </v-sheet>
          </div>

          <!-- Function error highlight -->
          <div v-if="getFunctionError(selectedEvent.context)" class="mt-4">
            <v-alert type="error" dense>
              <div class="font-weight-bold">{{ getFunctionError(selectedEvent.context).errorType }}</div>
              <div>{{ getFunctionError(selectedEvent.context).errorMessage }}</div>
            </v-alert>
          </div>

          <!-- Function logs/traceback -->
          <div v-if="selectedEvent.context && selectedEvent.context.logs" class="mt-4">
            <div class="font-weight-bold mb-2">{{ $translate('Log') }}</div>
            <v-sheet class="pa-3 grey lighten-4" rounded>
              <pre class="context-pre">{{ selectedEvent.context.logs }}</pre>
            </v-sheet>
          </div>

          <!-- Other context -->
          <div v-if="selectedEvent.context" class="mt-4">
            <div class="font-weight-bold mb-2">{{ $translate('Context') }}</div>
            <v-sheet class="pa-3 grey lighten-4" rounded>
              <pre class="context-pre">{{ formatContextClean(selectedEvent.context) }}</pre>
            </v-sheet>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="hasMatchingFunction(selectedEvent)"
            color="primary"
            outlined
            @click="goToCode(selectedEvent)"
          >
            <v-icon left small>mdi-code-tags</v-icon>
            {{ $translate('Go to code') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectedEvent.state === 'new'"
            color="warning"
            @click="changeEventState(selectedEvent, 'acknowledged')"
          >
            {{ $translate('Mark Acknowledged') }}
          </v-btn>
          <v-btn
            v-if="selectedEvent.state !== 'resolved'"
            color="success"
            @click="changeEventState(selectedEvent, 'resolved')"
          >
            {{ $translate('Mark Resolved') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk state change dialog -->
    <v-dialog v-model="showBulkDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $translate('Change State for') }} {{ selectedEvents.length }} {{ $translate('events') }}</v-card-title>
        <v-card-text>
          <v-btn
            v-for="state in ['new', 'acknowledged', 'resolved']"
            :key="state"
            :color="getStateColor(state)"
            outlined
            block
            class="mb-2"
            @click="bulkChangeState(state)"
          >
            {{ $translate(state) }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $translate('Delete events') }}</v-card-title>
        <v-card-text>
          {{ $translate('Are you sure you want to delete') }} {{ selectedEvents.length }} {{ $translate('events') }}?
          {{ $translate('This cannot be undone.') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">{{ $translate('Cancel') }}</v-btn>
          <v-btn color="error" @click="deleteEvents">{{ $translate('Delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Event not found dialog -->
    <v-dialog v-model="showEventNotFoundDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $translate('Event not found') }}</v-card-title>
        <v-card-text>
          {{ $translate('The event may have been deleted.') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showEventNotFoundDialog = false">{{ $translate('OK') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'event-list',
  props: {
    addonId: {
      type: String,
      required: true
    },
    webhooks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      events: [],
      total: 0,
      selectedEvents: [],
      selectedEvent: null,
      showDetailsDialog: false,
      showBulkDialog: false,
      showDeleteDialog: false,
      showEventNotFoundDialog: false,
      filters: {
        state: [],
        level: []
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 25
      },
      stateOptionsBase: ['new', 'acknowledged', 'resolved'],
      levelOptionsBase: ['error', 'warning', 'info']
    }
  },
  computed: {
    stateOptions() {
      return this.stateOptionsBase.map(v => ({ text: this.$translate(v), value: v }));
    },
    levelOptions() {
      return this.levelOptionsBase.map(v => ({ text: this.$translate(v), value: v }));
    },
    headers() {
      return [
        { text: this.$translate('Time'), value: 'created' },
        { text: this.$translate('Level'), value: 'level' },
        { text: this.$translate('Source'), value: 'source' },
        { text: this.$translate('Message'), value: 'message', cellClass: 'message-cell' },
        { text: this.$translate('Events'), value: 'occurrence_count' },
        { text: this.$translate('State'), value: 'state' }
      ];
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.updateUrlParams();
        this.loadEvents();
      },
      deep: true
    },
    filters: {
      handler() {
        this.updateUrlParams();
      },
      deep: true
    },
    addonId() {
      this.loadEvents();
    },
    selectedEvent(event) {
      const query = { ...this.$route.query };
      if (event) {
        query.event = event.id;
      } else {
        delete query.event;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    showDetailsDialog(visible) {
      if (!visible) {
        this.selectedEvent = null;
      }
    },
    '$route.query.tab'(newTab) {
      // Refresh when switching to log tab
      if (newTab === 'log') {
        this.loadEvents();
      }
    }
  },
  mounted() {
    this.readUrlParams();
    this.loadEventsAndOpenFromUrl();
  },
  methods: {
    async loadEventsAndOpenFromUrl() {
      await this.loadEvents();
      // Open event from URL if specified
      const eventId = this.$route.query.event;
      if (eventId) {
        let event = this.events.find(e => String(e.id) === String(eventId));
        if (!event) {
          // Event not in current page, try to fetch it directly
          event = await this.fetchEventById(eventId);
        }
        if (event) {
          this.showEventDetails(event);
        } else {
          // Remove event from URL to avoid showing dialog again
          const query = { ...this.$route.query };
          delete query.event;
          this.$router.replace({ query }).catch(() => {});
          this.showEventNotFoundDialog = true;
        }
      }
    },
    readUrlParams() {
      const query = this.$route.query;
      if (query.state) {
        this.filters.state = query.state.split(',');
      }
      if (query.level) {
        this.filters.level = query.level.split(',');
      }
      if (query.sort_by) {
        this.tableOptions.sortBy = [query.sort_by];
        this.tableOptions.sortDesc = [query.sort_desc === '1'];
      }
    },

    updateUrlParams() {
      const query = { ...this.$route.query };

      // Update filter params
      if (this.filters.state && this.filters.state.length > 0) {
        query.state = this.filters.state.join(',');
      } else {
        delete query.state;
      }
      if (this.filters.level && this.filters.level.length > 0) {
        query.level = this.filters.level.join(',');
      } else {
        delete query.level;
      }

      // Update sort params
      const { sortBy, sortDesc } = this.tableOptions;
      if (sortBy && sortBy.length > 0) {
        query.sort_by = sortBy[0];
        query.sort_desc = sortDesc[0] ? '1' : '0';
      } else {
        delete query.sort_by;
        delete query.sort_desc;
      }

      this.$router.replace({ query }).catch(() => {});
    },

    async loadEvents() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
        const offset = (page - 1) * itemsPerPage;

        let url = `/api/addon/supplier/events/get?addon_id=${this.addonId}&limit=${itemsPerPage}&offset=${offset}`;
        if (this.filters.state && this.filters.state.length > 0) {
          url += `&state=${this.filters.state.join(',')}`;
        }
        if (this.filters.level && this.filters.level.length > 0) {
          url += `&level=${this.filters.level.join(',')}`;
        }
        if (sortBy && sortBy.length > 0) {
          url += `&sort_by=${sortBy[0]}&sort_desc=${sortDesc[0] ? '1' : '0'}`;
        }

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          this.events = data.events;
          this.total = data.total;
        }
      } catch (e) {
        console.error('Failed to load events:', e);
      } finally {
        this.loading = false;
      }
    },

    async changeEventState(event, newState) {
      try {
        const response = await fetch('/api/addon/supplier/event/state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_id: event.id, state: newState })
        });
        if (response.ok) {
          // Update the event in the events array by ID (in case selectedEvent is a stale reference)
          const eventInList = this.events.find(e => e.id === event.id);
          if (eventInList) {
            eventInList.state = newState;
          }
          this.showDetailsDialog = false;
        }
      } catch (e) {
        console.error('Failed to change event state:', e);
      }
    },

    async bulkChangeState(newState) {
      try {
        const eventIds = this.selectedEvents.map(e => e.id);
        const response = await fetch('/api/addon/supplier/events/bulk/state', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_ids: eventIds, state: newState })
        });
        if (response.ok) {
          // Update events in the events array by ID (in case selectedEvents contains stale references)
          eventIds.forEach(id => {
            const eventInList = this.events.find(e => e.id === id);
            if (eventInList) {
              eventInList.state = newState;
            }
          });
          this.selectedEvents = [];
          this.showBulkDialog = false;
        }
      } catch (e) {
        console.error('Failed to bulk change state:', e);
      }
    },

    async deleteEvents() {
      try {
        const eventIds = this.selectedEvents.map(e => e.id);
        const response = await fetch('/api/addon/supplier/events/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event_ids: eventIds })
        });
        if (response.ok) {
          this.selectedEvents = [];
          this.showDeleteDialog = false;
          this.loadEvents();
        }
      } catch (e) {
        console.error('Failed to delete events:', e);
      }
    },

    showEventDetails(event) {
      this.selectedEvent = event;
      this.showDetailsDialog = true;
    },

    async fetchEventById(eventId) {
      try {
        const response = await fetch(`/api/addon/supplier/events/get?addon_id=${this.addonId}&event_id=${eventId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.events && data.events.length > 0) {
            return data.events[0];
          }
        }
      } catch (e) {
        console.error('Failed to fetch event:', e);
      }
      return null;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    },

    formatDateFull(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleString('sv-SE');
    },

    truncateMessage(message) {
      if (!message) return '';
      return message.length > 80 ? message.substring(0, 80) + '...' : message;
    },

    formatContext(context) {
      if (!context) return '';
      return JSON.stringify(context, null, 2);
    },

    getFunctionError(context) {
      if (!context || !context.error_detail) return null;
      try {
        // error_detail might be a JSON string
        const err = typeof context.error_detail === 'string'
          ? JSON.parse(context.error_detail)
          : context.error_detail;
        if (err.errorMessage || err.errorType) {
          return err;
        }
      } catch (e) {
        // Not valid JSON, return as-is
        return { errorMessage: context.error_detail, errorType: 'Error' };
      }
      return null;
    },

    formatContextClean(context) {
      if (!context) return '';
      // Remove error_detail and logs from display (shown separately)
      const clean = { ...context };
      delete clean.error_detail;
      delete clean.logs;
      // Truncate webhook_url if it's a long hash
      if (clean.webhook_url && clean.webhook_url.length > 40) {
        clean.webhook_url = clean.webhook_url.substring(0, 16) + '...';
      }
      return JSON.stringify(clean, null, 2);
    },

    getLevelColor(level) {
      switch (level) {
        case 'error': return 'error';
        case 'warning': return 'warning';
        case 'info': return 'info';
        default: return 'grey';
      }
    },

    getStateColor(state) {
      switch (state) {
        case 'new': return 'error';
        case 'acknowledged': return 'warning';
        case 'resolved': return 'success';
        default: return 'grey';
      }
    },

    hasMatchingFunction(event) {
      // function_id is set for function-based errors (webhooks, APIs, tasks, mailing handlers)
      // It's the function identifier used to navigate to code
      return event && event.context && event.context.function_id;
    },

    goToCode(event) {
      if (this.hasMatchingFunction(event)) {
        this.showDetailsDialog = false;
        this.$emit('go-to-function', event.context.function_id);
      }
    }
  }
}
</script>

<style scoped>
::v-deep .message-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-pre, .context-pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 13px;
  margin: 0;
}


.event-details-content {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
